import { animate, motion, useMotionValue, useTransform } from 'motion/react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BiologicalAgeSummary } from '../../../../../api/generated-catch-backend/model';

type BiologicalAgeCardProps = {
  biologicalAgeSummary: BiologicalAgeSummary;
  biologicalAgeSlug: string;
};

const yearOrYears = (num: number) => {
  if (num === 1) return 'year';
  return 'years';
};

export function BiologicalAgeCard({ biologicalAgeSummary, biologicalAgeSlug }: BiologicalAgeCardProps) {
  const biologicalAge = biologicalAgeSummary.biologicalAge;
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/labs/biomarkers/${biologicalAgeSlug}`);
  };

  const count = useMotionValue(0);
  const rounded = useTransform(() => Math.round(count.get()));

  useEffect(() => {
    const controls = animate(count, biologicalAge || 0, { duration: 3 });
    return () => controls.stop();
  });

  const summaryText = (() => {
    const className = 'text-center leading-[1.3] text-white';
    const { ageDiff } = biologicalAgeSummary;
    if (ageDiff === null) {
      return '';
    }
    if (biologicalAgeSummary.ageDiff === 0) {
      return (
        <p className={className}>
          You're <span className="font-bold">the same age</span> as your calendar age
        </p>
      );
    } else if (ageDiff > 0) {
      return (
        <p className={className}>
          You're{' '}
          <span className="font-bold">
            {ageDiff} {yearOrYears(ageDiff)} younger{' '}
          </span>{' '}
          than your calendar age{' '}
        </p>
      );
    } else if (ageDiff < 0) {
      return (
        <p className={className}>
          {' '}
          You're{' '}
          <span className="font-bold">
            {Math.abs(ageDiff)} {yearOrYears(Math.abs(ageDiff))} older
          </span>{' '}
          than your calendar age
        </p>
      );
    }
    return '';
  })();

  return (
    <div className="dashboard-reset group relative w-full self-start overflow-hidden rounded-[10px] bg-[#183344] text-left transition-all hover:shadow-lg focus:ring-2 focus:ring-[#183344] focus:ring-offset-2 focus:outline-none">
      {/* Background Video */}
      <video
        src="/videos/cells.mp4"
        autoPlay
        loop
        muted
        playsInline
        className="absolute inset-0 z-0 h-full w-full object-cover"
        style={{ height: '100%' }}
      />

      {/* Dark overlay for better text contrast */}
      <div className="absolute inset-0 z-[1] bg-[#183344]/60" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center px-5 pt-10 pb-8">
        {/* Arrow button in top right */}
        <div className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/20 text-white transition-colors group-hover:bg-white/30">
          <button
            onClick={handleClick}
            className="flex h-[39px] w-[39px] shrink-0 items-center justify-center rounded-full bg-[#F0F5F6] transition-opacity hover:opacity-70"
            type="button"
            aria-label={`View details for biological age`}
          >
            <img src="/images/icons/arrow right.svg" alt="" className="h-[9px] w-[15px]" />
          </button>
        </div>

        {/* Biological Age Number */}
        <div className="text-[80px] leading-none font-bold text-white">
          <motion.pre>{rounded}</motion.pre>
        </div>

        {/* Title */}
        <h3 className="mt-2 text-center text-[24px] leading-[1.3] font-bold text-white">Your Biological Age</h3>
        <p>{summaryText}</p>
      </div>
    </div>
  );
}
